import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, ArrowRight, Eye, EyeOff, Send, Zap, BarChart2, ShieldCheck, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const Feature = ({ icon: Icon, color, text }) => (
  <div className="flex items-center gap-3">
    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.10)" }}>
      <Icon className="w-4 h-4" style={{ color }} />
    </div>
    <span className="text-sm" style={{ color: "rgba(255,255,255,0.75)" }}>{text}</span>
  </div>
);

export default function Signin() {
  const { signin } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState(loc.state?.email || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    const cleanPassword = password.trim();
    if (!cleanEmail || !cleanPassword) { toast.error("Email and password required"); return; }
    setLoading(true);
    try {
      const loggedUser = await signin(cleanEmail, cleanPassword);
      const isMaster = loggedUser?.role === "master" || loggedUser?.role === "admin";
      toast.success(isMaster ? "Signed in as Master Admin!" : "Signed in successfully!");
      const target = loc.state?.from?.pathname || (isMaster ? "/admin" : "/");
      nav(target, { replace: true });
    }
    catch (err) {
      if (err?.response?.data?.detail === "EMAIL_NOT_VERIFIED") {
        toast.error("Please verify your email address to activate your 7-day trial.");
        nav(`/verify-email?email=${encodeURIComponent(cleanEmail)}`);
        return;
      }
      toast.error(err?.response?.data?.detail || "Sign in failed");
    }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex" style={{ background: "#0D0F14" }}>

      {/* Left panel */}
      <div className="hidden lg:flex flex-1 flex-col p-12 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0A0F29 0%, #0D1640 60%, #0A0F29 100%)" }}>
        {/* Grid bg */}
        <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
        {/* Glow */}
        <div className="absolute top-1/4 right-0 w-96 h-96 rounded-full blur-3xl" style={{ background: "rgba(0, 68, 255, 0.15)" }} />
        <div className="absolute bottom-0 left-1/4 w-64 h-64 rounded-full blur-3xl" style={{ background: "rgba(240, 90, 40, 0.10)" }} />

        <div className="relative z-10 flex-1 flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--brand)" }}>
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight text-white" style={{ fontFamily: "var(--font-heading)" }}>LaunchDeck</span>
          </div>

          <div>
            <h1 className="font-black leading-none mb-6" style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)", fontFamily: "var(--font-heading)", color: "#FFFFFF", letterSpacing: "-0.04em" }}>
              Your full-stack<br />marketing OS.
            </h1>
            <p className="mb-10 text-base" style={{ color: "rgba(255,255,255,0.55)" }}>
              Email · Social · Blog · Automations.<br />All AI-powered. All in one workspace.
            </p>
            <div className="space-y-3">
              <Feature icon={Send} color="#4D7CFF" text="AI newsletter campaigns with open & click tracking" />
              <Feature icon={Zap} color="#F97316" text="Smart automations that fire on real triggers" />
              <Feature icon={BarChart2} color="#22C55E" text="Unified dashboard with live engagement stats" />
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm" style={{ color: "rgba(255,255,255,0.30)" }}>
            <span className="w-2 h-2 rounded-full credits-dot" style={{ background: "#22C55E" }} />
            All systems operational
          </div>
        </div>
      </div>

      {/* Right panel (form) */}
      <div className="flex-1 flex items-center justify-center p-8" style={{ background: "#F4F5F7" }}>
        <div className="w-full max-w-md space-y-8 fade-in">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 lg:hidden">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "var(--brand)" }}>
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-lg tracking-tight" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>LaunchDeck</span>
          </div>

          <div>
            <h2 className="text-3xl font-black tracking-tight" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>
              Welcome back
            </h2>
            <p className="mt-1 text-sm" style={{ color: "var(--text-soft)" }}>Sign in to your workspace</p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="signin-card">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-semibold" style={{ color: "var(--text)" }}>Email</Label>
              <Input
                data-testid="signin-email"
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="h-11"
                style={{ background: "var(--surface)", borderColor: "var(--border)" }}
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-semibold" style={{ color: "var(--text)" }}>Password</Label>
                <Link to="/forgot-password" className="text-xs font-semibold hover:underline" style={{ color: "var(--brand)" }} data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  data-testid="signin-password"
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 pr-11"
                  style={{ background: "var(--surface)", borderColor: "var(--border)" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-3"
                  style={{ color: "var(--text-soft)" }}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              data-testid="signin-submit"
              type="submit"
              disabled={loading}
              className="w-full h-11 gap-2 font-semibold text-white"
              style={{ background: "var(--brand)", borderRadius: "var(--radius)" }}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="text-sm text-center" style={{ color: "var(--text-soft)" }}>
            New here?{" "}
            <Link to="/signup" className="font-semibold hover:underline" style={{ color: "var(--brand)" }} data-testid="goto-signup">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
